import './StatusChip.css';
import { NX_STATUS_COLOR } from '../tokens';
import type { StatusKind } from '../tokens';

export function StatusChip({
  status,
  label,
  pulse = false,
  title,
}: {
  status: StatusKind;
  label?: string;
  pulse?: boolean;
  /** Hover text. For a chip whose two-word label is true but needs a
   *  sentence to be USEFUL -- "via worker" on a Kubernetes site is
   *  correct and means nothing until you know the platform tier has no
   *  route to a site broker by design. */
  title?: string;
}) {
  // Defensive fallback: if a caller passes an unknown status (e.g. from
  // an API response that doesn't match the union), fall back to 'offline'
  // rather than crashing on `status.toUpperCase()` or rendering a chip
  // with no colour. The colour lookup uses the same guard.
  const safeStatus: StatusKind =
    status && Object.prototype.hasOwnProperty.call(NX_STATUS_COLOR, status) ? status : 'offline';
  const colour = NX_STATUS_COLOR[safeStatus] ?? NX_STATUS_COLOR.offline;
  const text = label ?? safeStatus.toUpperCase();
  // `moving` (a door or lift-door mid-transit) always flashes,
  // regardless of the `pulse` prop — it's a transient state worth
  // catching at a glance, not something callers should be able to
  // forget to opt into. Distinct from `pulse` (a subtle dot-only
  // animation used for e.g. an active robot) — this flashes the
  // whole chip.
  const flashing = safeStatus === 'moving';
  return (
    <span
      className={`nx-status-chip nx-status-chip--${safeStatus}${pulse ? ' nx-status-chip--pulse' : ''}${flashing ? ' nx-status-chip--flash' : ''}`}
      style={{ ['--nx-chip-color' as string]: colour }}
      role="status"
      aria-label={`Status: ${text}`}
      title={title}
    >
      <span className="nx-status-chip__dot" aria-hidden="true" />
      <span className="nx-status-chip__label">{text}</span>
    </span>
  );
}